import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosInstance';
import { Button } from "@/components/ui/button";
import { PageLoader } from '@/components/ui/page-loader';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BASE_URL } from '../config/constants';
import { toast } from 'sonner';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Cropper } from '@/components/ui/image-crop';
import { Upload, RefreshCw, Trash2, Music2, CalendarDays, Pencil, ArrowLeft, ImageIcon } from 'lucide-react';
import { usePlan } from '@/context/PlanContext';
import { PlanLimitBadge, GracePeriodBanner, PlanItemBadge } from '@/components/PlanComponents';
import { normalizeUrl } from '@/utils/urlInput';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

interface Banner {
  _id: string;
  title: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  image: string;
  order: number;
  isActive: boolean;
  sourceType?: 'custom' | 'showcase' | 'toursEvent';
}

interface ShowcaseItem {
  _id: string;
  title: string;
  thumbnail?: string;
  youtube?: string;
  soundcloudUrl?: string;
  spotifyUrl?: string;
  createdAt?: string;
  isActive?: boolean;
}

interface ToursEventItem {
  _id: string;
  eventName: string;
  description?: string;
  date?: string;
  endDate?: string;
  venue?: string;
  city?: string;
  image?: string;
  ctaText?: string;
  ctaLink?: string;
  createdAt?: string;
  isActive?: boolean;
  eventStatus?: 'Upcoming' | 'Completed' | 'Cancelled';
  computedStatus?: 'Upcoming' | 'Active' | 'Completed' | 'Cancelled';
}

type AddStep = 'choose' | 'showcase' | 'tours' | 'custom';

const extractYouTubeVideoId = (url: string): string | null => {
  const match = url.match(/(?:youtube\.com\/.*[?&]v=|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1] : null;
};

const latestFive = <T extends { createdAt?: string; order?: number }>(items: T[]): T[] =>
  [...items]
    .sort((a, b) => {
      // Prefer sorting by order field if available, otherwise by createdAt
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return (Date.parse(b.createdAt ?? '') || 0) - (Date.parse(a.createdAt ?? '') || 0);
    })
    .slice(0, 5);

const getShowcaseLink = (item: ShowcaseItem): string =>
  item.youtube || item.soundcloudUrl || item.spotifyUrl || '';

const getShowcaseImage = (item: ShowcaseItem): string => {
  if (item.thumbnail) return item.thumbnail;
  const videoId = item.youtube ? extractYouTubeVideoId(item.youtube) : null;
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
};

const isCompletedEvent = (event: ToursEventItem): boolean => {
  if (event.computedStatus) return event.computedStatus === 'Completed';
  if (event.eventStatus === 'Completed') return true;
  const finalDate = event.endDate || event.date;
  return Boolean(finalDate && Date.parse(finalDate) < Date.now());
};

interface SortableTableRowProps {
  banner: Banner;
  index: number;
  handleEdit: (banner: Banner) => void;
  handleToggleActive: (id: string, currentStatus: boolean) => void;
  setDeleteId: (id: string) => void;
  togglingId: string | null;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({
  banner,
  index,
  handleEdit,
  handleToggleActive,
  setDeleteId,
  togglingId,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: banner._id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className="transition-colors">
      <TableCell className="w-10 select-none cursor-grab">
        <div className="flex items-center gap-2" {...attributes} {...listeners}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
            <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
          </svg>
          <span className="w-4 text-muted-foreground font-medium">{banner.order + 1}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="font-semibold">{banner.title}</div>
        {banner.sourceType && banner.sourceType !== 'custom' && (
          <div className="mt-0.5 text-xs font-normal text-muted-foreground">
            {banner.sourceType === 'showcase' ? 'From Showcase' : 'From Tours & Events'}
          </div>
        )}
      </TableCell>
      <TableCell className="max-w-xs truncate text-sm">{banner.description}</TableCell>
      <TableCell>
        <div className="text-sm">
          <div className="font-medium">{banner.ctaText}</div>
          <div className="text-muted-foreground text-xs truncate max-w-[200px]">{banner.ctaLink}</div>
        </div>
      </TableCell>
      <TableCell>
        {banner.image && (
          <img 
            src={banner.image} 
            alt="banner" 
            className="w-20 h-12 object-cover rounded-lg" 
          />
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          <Switch
            checked={banner.isActive}
            onCheckedChange={() => handleToggleActive(banner._id, banner.isActive)}
            disabled={togglingId === banner._id}
            aria-label="Toggle banner active state"
          />
          <span className={banner.isActive ? 'status-label-active' : 'status-label-inactive'}>
            {banner.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <PlanItemBadge resourceType="banners" index={index} />
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon-sm" 
            onClick={() => handleEdit(banner)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </Button>
          <Button 
            variant="destructive" 
            size="icon-sm" 
            onClick={() => setDeleteId(banner._id)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const emptyBanner: Omit<Banner, '_id'> = {
  title: '',
  description: '',
  ctaText: '',
  ctaLink: '',
  image: '',
  order: 0,
  isActive: true
};

// (normalizeUrl moved to utils/urlInput.ts — shared with the management
// section forms' https://-prefixed URL inputs.)

interface BannerManagerProps {
  domain: string;
}

const BannerManager: React.FC<BannerManagerProps> = ({ domain }) => {
  const { canAdd, openUpgradeModal, adjustUsage } = usePlan();
  const location = useLocation();
  const navigate = useNavigate();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<Banner, '_id'>>(emptyBanner);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [urlSuggestion, setUrlSuggestion] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [uploadStep, setUploadStep] = useState<'idle' | 'selecting' | 'cropping'>('idle');
  const [addStep, setAddStep] = useState<AddStep>('choose');
  const [sourceItems, setSourceItems] = useState<(ShowcaseItem | ToursEventItem)[]>([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceAddingId, setSourceAddingId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sourceRequestRef = useRef(0);


  const fetchBanners = () => {
    setLoading(true);
    axios.get(`${BASE_URL}/api/banner`)
      .then(res => setBanners(res.data))
      .finally(() => setLoading(false));
  };

  const resetUploadState = () => {
    setUploadStep('idle');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const openCropperForExisting = () => {
    if (form.image) {
      fetch(form.image)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'banner-image.png', { type: blob.type });
          setSelectedFile(file);
          setUploadStep('cropping');
        })
        .catch(() => {
          toast.error('Failed to load image for editing');
        });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validFormats = ['image/png', 'image/jpeg'];
    const maxSize = 2 * 1024 * 1024;
    
    if (!validFormats.includes(file.type)) {
      setError('Only .png and .jpeg files are allowed.');
      toast.error('Only .png and .jpeg files are allowed.');
      return;
    }
    
    if (file.size > maxSize) {
      setError('File size must be less than 2MB.');
      toast.error('File size must be less than 2MB.');
      return;
    }
    
    setError('');
    setSelectedFile(file);
    setUploadStep('cropping');
  };

  const handleCropComplete = async (croppedImage: string) => {
    if (!croppedImage) return;
    
    setUploading(true);
    try {
      const response = await fetch(croppedImage);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append('file', blob, 'cropped-banner.png');
      
      const res = await axios.post(BASE_URL + '/api/images/upload', formData);
      setForm((prev) => ({ ...prev, image: res.data.url }));
      resetUploadState();
      const isUpdate = form.image ? true : false;
      toast.success(isUpdate ? 'Image updated successfully!' : 'Image uploaded successfully!');
    } catch (err) {
      setError('Image upload failed.');
      toast.error('Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, [domain]);

  // Auto-open the "Add Banner" dialog when navigated here from the dashboard
  // card (state.openAdd). Mirrors the Add button's plan-limit behaviour, then
  // clears the navigation state so it doesn't re-trigger on refresh/back.
  useEffect(() => {
    if (!(location.state as { openAdd?: boolean } | null)?.openAdd) return;
    navigate(location.pathname, { replace: true, state: null });
    if (!canAdd('banners')) {
      openUpgradeModal('banners');
    } else {
      setAddOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const openSourceList = async (step: 'showcase' | 'tours') => {
    const requestId = ++sourceRequestRef.current;
    setAddStep(step);
    setSourceLoading(true);
    setSourceItems([]);
    try {
      const endpoint = step === 'showcase' ? '/api/showcase' : '/api/tours-events';
      const res = await axios.get(`${BASE_URL}${endpoint}`);
      if (requestId === sourceRequestRef.current) {
        const items = Array.isArray(res.data) ? res.data : [];
        const eligibleItems = step === 'tours'
          ? (items as ToursEventItem[]).filter(event => !isCompletedEvent(event))
          : items;
        setSourceItems(latestFive(eligibleItems));
      }
    } catch (err: any) {
      if (requestId === sourceRequestRef.current) {
        toast.error(err.response?.data?.message || 'Failed to load items');
        setAddStep('choose');
      }
    } finally {
      if (requestId === sourceRequestRef.current) {
        setSourceLoading(false);
      }
    }
  };

  const returnToSourceChooser = () => {
    sourceRequestRef.current += 1;
    setAddStep('choose');
    setSourceItems([]);
    setSourceLoading(false);
  };

  const handleAddFromSource = async (sourceType: 'showcase' | 'toursEvent', sourceId: string) => {
    setSourceAddingId(sourceId);
    try {
      const response = await axios.post(`${BASE_URL}/api/banner`, { sourceType, sourceId });
      handleCloseDialog(false);
      toast.success('Banner added successfully!');
      adjustUsage('banners', 1);
      if (response.data) {
        setBanners(prev => [...prev, response.data]);
      } else {
        fetchBanners();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add banner');
    } finally {
      setSourceAddingId(null);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Special handling for ctaLink field
    if (name === 'ctaLink') {
      const { url, suggestion } = normalizeUrl(value);
      setForm(prev => ({ ...prev, [name]: url }));
      setUrlSuggestion(suggestion);
      if (suggestion) {
        toast.info(suggestion, { duration: 4000 });
      }
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
      setUrlSuggestion(null);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/banner`, {
        ...form,
        order: banners.length,
        sourceType: 'custom',
      });
      // Close dialog immediately
      handleCloseDialog(false);
      toast.success('Banner added successfully!');
      adjustUsage('banners', 1);

      // Add to local state optimistically
      if (response.data) {
        setBanners(prev => [...prev, response.data]);
      } else {
        fetchBanners();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to add banner';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner: Banner) => {
    setEditId(banner._id);
    setForm({
      title: banner.title,
      description: banner.description,
      ctaText: banner.ctaText,
      ctaLink: banner.ctaLink,
      image: banner.image,
      order: banner.order,
      isActive: banner.isActive
    });
    setEditOpen(true);
    setError('');
  };

  const handleCloseDialog = (isEditDialog: boolean) => {
    if (isEditDialog) {
      setEditOpen(false);
      setEditId(null);
    } else {
      setAddOpen(false);
    }
    setForm(emptyBanner);
    setUrlSuggestion(null);
    setError('');
    returnToSourceChooser();
    setSourceAddingId(null);
    resetUploadState();
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const currentEditId = editId;
    const { isActive, ...updatePayload } = form;
    try {
      const response = await axios.put(`${BASE_URL}/api/banner/${editId}`, updatePayload);
      // Close dialog immediately
      handleCloseDialog(true);
      toast.success('Banner updated successfully!');

      // Update local state optimistically
      if (response.data) {
        setBanners(prev => prev.map(banner =>
          banner._id === currentEditId ? { ...banner, ...response.data } : banner
        ));
      } else {
        fetchBanners();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update banner';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    // Optimistic update - update UI immediately
    setBanners(prev => prev.map(banner => 
      banner._id === id ? { ...banner, isActive: !currentStatus } : banner
    ));
    setTogglingId(id);
    
    try {
      await axios.patch(`${BASE_URL}/api/banner/${id}/toggle-status`, { isActive: !currentStatus });
      toast.success(`Banner ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
    } catch (err: any) {
      // Revert on error
      setBanners(prev => prev.map(banner => 
        banner._id === id ? { ...banner, isActive: currentStatus } : banner
      ));
      const errorMessage = err.response?.data?.message || 'Failed to toggle banner status';
      toast.error(errorMessage);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const deletedBanner = banners.find(banner => banner._id === deleteId);
    // Optimistic removal
    setBanners(prev => prev.filter(banner => banner._id !== deleteId));
    setDeleteId(null);
    try {
      await axios.delete(`${BASE_URL}/api/banner/${deleteId}`);
      toast.success('Banner deleted successfully!');
      adjustUsage('banners', -1);

    } catch (err: any) {
      // Revert on error
      if (deletedBanner) {
        setBanners(prev => [...prev, deletedBanner].sort((a, b) => a.order - b.order));
      }
      const errorMessage = err.response?.data?.message || 'Failed to delete banner';
      toast.error(errorMessage);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = banners.findIndex((item) => item._id === active.id);
      const newIndex = banners.findIndex((item) => item._id === over.id);

      try {
        await axios.put(
          `${BASE_URL}/api/banner/${active.id}/reorder`,
          { newOrder: newIndex }
        );
        setBanners((items) => arrayMove(items, oldIndex, newIndex));
        toast.success('Banner reordered successfully!');
      } catch (err: any) {
        setBanners((items) => arrayMove(items, newIndex, oldIndex)); // Revert the order if API call fails
        toast.error(err.response?.data?.message || 'Failed to reorder banner');
      }
    }
  };

  const renderForm = (onSubmit: (e: React.FormEvent) => Promise<void>) => (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="form-group">
        <Label htmlFor="title" className="form-label">Title<span className="text-red-500">*</span></Label>
        <Input 
          id="title"
          name="title" 
          value={form.title} 
          onChange={handleFormChange} 
          placeholder="Enter title here..."
          required 
        />
      </div>
      <div className="form-group">
        <Label htmlFor="description" className="form-label">Description<span className="text-red-500">*</span></Label>
        <Textarea 
          id="description"
          name="description" 
          value={form.description} 
          onChange={handleFormChange} 
          placeholder="Enter description here..."
          required 
          className="h-24"
        />
      </div>
      <div className="form-group">
        <Label htmlFor="ctaText" className="form-label">CTA Text<span className="text-red-500">*</span></Label>
        <Input 
          id="ctaText"
          name="ctaText" 
          value={form.ctaText} 
          onChange={handleFormChange} 
          required 
          placeholder="Learn More, Book Now, View Now, etc."
        />
      </div>
      <div className="form-group">
        <Label htmlFor="ctaLink" className="form-label">CTA Link<span className="text-red-500">*</span></Label>
        <Input 
          id="ctaLink"
          name="ctaLink" 
          value={(form.ctaLink || '').replace(/^https?:\/\//, '')} 
          onChange={(e) => {
            const rawValue = e.target.value.replace(/^https?:\/\//, '');
            handleFormChange({ ...e, target: { ...e.target, name: 'ctaLink', value: rawValue } } as any);
          }}
          required 
          prefix={<span className="font-mono text-xs whitespace-nowrap">https://</span>}
          placeholder="domain.com"
        />
        <div className="mt-2 text-xs space-y-1.5">
          <div className="text-muted-foreground">
            <strong className="font-semibold">Example:</strong> Enter <code className="bg-secondary px-1.5 py-0.5 rounded">domain.com</code> → Saves as <code className="bg-secondary px-1.5 py-0.5 rounded">https://domain.com</code>
          </div>
          {urlSuggestion && (
            <div className="bg-info-light border border-info/20 text-info p-2.5 rounded-lg">
              ℹ️ {urlSuggestion}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label className="form-label">Banner Image (16:9)<span className="text-red-500">*</span></Label>
        {uploadStep === 'idle' ? (
          !form.image ? (
            <div className="w-full">
              <Card
                className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-secondary/50 transition-colors"
                onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('bg-muted/50');
                }}
                onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                  e.currentTarget.classList.remove('bg-muted/50');
                }}
                onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-muted/50');
                  const files = e.dataTransfer.files;
                  if (files.length > 0) {
                    const event = {
                      target: { files }
                    } as any;
                    handleFileChange(event);
                  }
                }}
                onClick={openFileUpload}
              >
                <div className="grid space-y-3">
                  <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                    <Upload className="size-5" />
                    <div>
                      Drop image here or{" "}
                      <Button
                        type="button"
                        variant="link"
                        className="text-primary p-0 h-auto font-normal"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFileUpload();
                        }}
                      >
                        browse
                      </Button>{" "}
                      to upload
                    </div>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleFileChange}
                />
                <span className="text-xs text-muted-foreground mt-1 block">
                  Supported: JPG, PNG (max 2 MB) | Aspect ratio: 16:9
                </span>
              </Card>
            </div>
          ) : (
            <div className="space-y-2">
              <Card
                className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={openCropperForExisting}
              >
                <div 
                  className="relative max-h-64 max-w-64 rounded overflow-hidden"
                  style={{
                    backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                    backgroundSize: '16px 16px',
                    backgroundPosition: '0 0, 8px 8px',
                    backgroundRepeat: 'repeat'
                  }}
                >
                  <img 
                    src={form.image} 
                    alt="uploaded" 
                    className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                    title="Click to edit image"
                  />
                </div>
                <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                  <Upload className="size-4" />
                  <span>Click to edit or re-upload</span>
                </div>
              </Card>
              <div className="flex gap-2">
                <Button 
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={openFileUpload}
                >
                  <RefreshCw className="size-3.5" />
                  Change
                </Button>
                <Button 
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-danger"
                  onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                >
                  <Trash2 className="size-3.5" />
                  Remove
                </Button>
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
              />
            </div>
          )
        ) : uploadStep === 'cropping' && selectedFile ? (
          <div className="space-y-2">
            <Card className="flex flex-col items-center">
              <div className="w-full border-b p-3">
                <p className="text-sm font-medium text-foreground text-center">Adjust crop area (16:9 Aspect) - Drag to move, edges to resize</p>
              </div>
              <div className="w-full flex justify-center py-3">
                <Cropper
                  file={selectedFile}
                  // aspect={16 / 9}
                  maxImageSize={2 * 1024 * 1024}
                  keepSelection={true}
                  onCrop={handleCropComplete}
                  onCropRef={cropperRef}
                />
              </div>
            </Card>
            <div className="flex gap-2">
              <Button 
                type="button"
                onClick={async () => {
                  await cropperRef.current?.applyCrop();
                }}
                disabled={uploading}
                size="sm"
                loading={uploading}
                loadingText="Uploading..."
              >
                {form.image ? 'Update' : 'Upload'}
              </Button>
              <Button 
                type="button"
                variant="outline"
                onClick={resetUploadState}
                disabled={uploading}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      {error && <div className="text-danger">{error}</div>}
      <Button type="submit" loading={saving} loadingText={editId ? 'Saving...' : 'Adding...'}>{editId ? 'Save' : 'Add'}</Button>
    </form>
  );

  const renderAddChooser = () => (
    <div className="grid gap-3 sm:grid-cols-3">
      <Button
        type="button"
        variant="outline"
        className="h-auto flex-row items-center justify-start gap-3 whitespace-normal p-4 text-left shadow-none hover:border-primary/40 hover:bg-primary-light/40 sm:min-h-36 sm:flex-col sm:items-start"
        onClick={() => openSourceList('showcase')}
      >
        <span className="flex size-9 items-center justify-center rounded-lg bg-primary-light text-primary">
          <Music2 className="size-5" />
        </span>
        <span>
          <span className="block font-semibold text-foreground">From Showcase</span>
          <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">Choose from your 5 latest items</span>
        </span>
      </Button>
      <Button
        type="button"
        variant="outline"
        className="h-auto flex-row items-center justify-start gap-3 whitespace-normal p-4 text-left shadow-none hover:border-primary/40 hover:bg-primary-light/40 sm:min-h-36 sm:flex-col sm:items-start"
        onClick={() => openSourceList('tours')}
      >
        <span className="flex size-9 items-center justify-center rounded-lg bg-info-light text-info">
          <CalendarDays className="size-5" />
        </span>
        <span>
          <span className="block font-semibold text-foreground">From Tours & Events</span>
          <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">Choose from your 5 latest events</span>
        </span>
      </Button>
      <Button
        type="button"
        variant="outline"
        className="h-auto flex-row items-center justify-start gap-3 whitespace-normal p-4 text-left shadow-none hover:border-primary/40 hover:bg-primary-light/40 sm:min-h-36 sm:flex-col sm:items-start"
        onClick={() => setAddStep('custom')}
      >
        <span className="flex size-9 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
          <Pencil className="size-5" />
        </span>
        <span>
          <span className="block font-semibold text-foreground">Custom</span>
          <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">Create a banner from scratch</span>
        </span>
      </Button>
    </div>
  );

  const renderSourceList = () => {
    const isShowcase = addStep === 'showcase';
    return (
      <div className="space-y-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 text-muted-foreground"
          onClick={returnToSourceChooser}
        >
          <ArrowLeft className="size-4" />
          Back
        </Button>
        {sourceLoading ? (
          <div className="py-8">
            <PageLoader label={isShowcase ? 'Loading showcase items...' : 'Loading tours & events...'} />
          </div>
        ) : sourceItems.length === 0 ? (
          <div className="rounded-xl border border-dashed px-6 py-10 text-center">
            {isShowcase ? <Music2 className="mx-auto mb-3 size-7 text-muted-foreground" /> : <CalendarDays className="mx-auto mb-3 size-7 text-muted-foreground" />}
            <p className="text-sm font-medium text-foreground">{isShowcase ? 'No showcase items yet' : 'No upcoming or active events'}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {isShowcase
                ? 'Add content in Showcase first, then return here.'
                : 'Completed events are not available for new banners.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {sourceItems.map((item) => {
              const showcase = item as ShowcaseItem;
              const tour = item as ToursEventItem;
              const title = isShowcase ? showcase.title : tour.eventName;
              const image = isShowcase
                ? getShowcaseImage(showcase)
                : tour.image || '';
              const subtitle = isShowcase
                ? (showcase.youtube ? 'YouTube' : showcase.soundcloudUrl ? 'SoundCloud' : showcase.spotifyUrl ? 'Spotify' : '')
                : [
                    [tour.venue, tour.city].filter(Boolean).join(', '),
                    tour.date ? new Date(tour.date).toLocaleDateString() : '',
                  ].filter(Boolean).join(' — ');
              const canUseItem = isShowcase
                ? Boolean(getShowcaseLink(showcase) && image)
                : Boolean(tour.image && tour.ctaLink);
              const status = isShowcase
                ? (showcase.isActive === false ? 'Inactive' : '')
                : tour.isActive === false ? 'Inactive' : tour.computedStatus || tour.eventStatus || '';
              return (
                <Card key={item._id} className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-3 p-3 sm:grid-cols-[5rem_minmax(0,1fr)_auto]">
                  {image ? (
                    <img src={image} alt="" className="h-14 w-20 rounded-lg object-cover" />
                  ) : (
                    <div className="flex h-14 w-20 items-center justify-center rounded-lg bg-secondary">
                      <ImageIcon className="size-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">{title}</div>
                    {subtitle && <div className="mt-1 truncate text-xs text-muted-foreground">{subtitle}</div>}
                    {status && <div className="mt-1 text-[11px] font-medium text-muted-foreground">{status}</div>}
                    {!canUseItem && (
                      <div className="mt-1 text-[11px] text-danger">
                        {isShowcase ? 'A media link and image are required' : 'An image and CTA link are required'}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="col-span-2 w-full sm:col-span-1 sm:w-auto"
                    disabled={!canUseItem || (sourceAddingId !== null && sourceAddingId !== item._id)}
                    loading={sourceAddingId === item._id}
                    loadingText="Adding..."
                    onClick={() => handleAddFromSource(isShowcase ? 'showcase' : 'toursEvent', item._id)}
                  >
                    Add banner
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <GracePeriodBanner />
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="page-title">Promo Banner Management</h2>
          <PlanLimitBadge resourceType="banners" />
        </div>
        <Dialog open={addOpen} onOpenChange={(open) => open ? setAddOpen(true) : handleCloseDialog(false)}>
          <DialogTrigger asChild>
            <Button
              onClick={(e) => {
                if (!canAdd('banners')) {
                  e.preventDefault();
                  e.stopPropagation();
                  openUpgradeModal('banners');
                }
              }}
            >
              Add Banner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {addStep === 'choose'
                  ? 'Add banner'
                  : addStep === 'showcase'
                  ? 'Add from Showcase'
                  : addStep === 'tours'
                  ? 'Add from Tours & Events'
                  : 'Create custom banner'}
              </DialogTitle>
              <DialogDescription>
                {addStep === 'choose'
                  ? 'Choose an existing item or create something custom.'
                  : addStep === 'showcase'
                  ? 'Select one of your 5 most recently added showcase items.'
                  : addStep === 'tours'
                  ? 'Select one of your 5 most recently added tours or events.'
                  : 'Add an image, message, and call-to-action link.'}
              </DialogDescription>
            </DialogHeader>
            {addStep === 'choose' ? (
              renderAddChooser()
            ) : addStep === 'custom' ? (
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-ml-2 text-muted-foreground"
                  onClick={() => {
                    setForm(emptyBanner);
                    setUrlSuggestion(null);
                    setError('');
                    resetUploadState();
                    returnToSourceChooser();
                  }}
                >
                  <ArrowLeft className="size-4" />
                  Back
                </Button>
                {renderForm(handleAdd)}
              </div>
            ) : (
              renderSourceList()
            )}
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="empty-state">
          <PageLoader label="Loading banners..." />
        </div>
      ) : banners.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Upload className="w-full h-full" />
          </div>
          <h3 className="empty-state-title">No banners found</h3>
          <p className="empty-state-description">Create your first banner to get started</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Description</TableHead>
                  <TableHead>CTA</TableHead>
                  <TableHead>Image</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <SortableContext items={banners.map(b => b._id)} strategy={verticalListSortingStrategy}>
                  {banners.map((banner, idx) => (
                    <SortableTableRow
                      key={banner._id}
                      banner={banner}
                      index={idx}
                      handleEdit={handleEdit}
                      handleToggleActive={handleToggleActive}
                      setDeleteId={setDeleteId}
                      togglingId={togglingId}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        )}

      <Dialog open={editOpen} onOpenChange={(open) => open ? setEditOpen(true) : handleCloseDialog(true)}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Banner</DialogTitle>
            <DialogDescription>Update the banner details including image, text, and call-to-action link</DialogDescription>
          </DialogHeader>
          {renderForm(handleEditSubmit)}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Banner"
        description="Are you sure you want to delete this banner? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default BannerManager;
